import { Box } from '@ublend-npm/aulaui-core';
import React from 'react';
import { Virtuoso } from 'react-virtuoso';

type InfiniteScrollerProps<T> = {
  items?: T[];
  loadMore: () => void;
  hasMore?: boolean;
  loading?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
  height: number | string;
  orientation?: 'vertical' | 'horizontal';
  footer?: React.ReactNode;
  /** Ref forwarded to the outer scroll Box — used by parent to attach a scroll listener */
  scrollerRef?: React.RefObject<HTMLDivElement>;
  /** Called on every scroll event of the outer container */
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  /** Called by Virtuoso when the visible range changes */
  onRangeChanged?: (range: { startIndex: number; endIndex: number }) => void;
  /** Optional data-tour hook on Virtuoso's scroll container (e.g. product tours) */
  dataTour?: string;
  /** Accessible name for the list region */
  ariaLabel?: string;
};

// Virtuoso's internal scroll container customised to support CSS scroll-snap.
// React.forwardRef is required by Virtuoso's components API.
const createSnapScroller = (dataTour?: string) => {
  const SnapScroller = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
  >((props, ref) => (
    <div
      {...props}
      ref={ref}
      {...(dataTour ? { 'data-tour': dataTour } : {})}
      style={{
        ...props.style,
        scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth',
        // Hide scrollbar (already hidden on the outer Box, but belt-and-suspenders)
        scrollbarWidth: 'none',
      }}
    />
  ));
  SnapScroller.displayName = 'SnapScroller';
  return SnapScroller;
};

const InfiniteScroller = <T,>({
  items = [],
  loadMore,
  hasMore = true,
  loading = false,
  renderItem,
  height,
  orientation = 'vertical',
  footer,
  scrollerRef,
  onScroll,
  onRangeChanged,
  dataTour,
  ariaLabel = 'Scrollable list',
}: InfiniteScrollerProps<T>): JSX.Element => {
  const isHorizontal = orientation === 'horizontal';
  // Keep Scroller identity stable for a given dataTour — react-virtuoso caches
  // component references; recreating only when the tour hook changes is safe.
  const SnapScroller = React.useMemo(
    () => createSnapScroller(dataTour),
    [dataTour]
  );

  return (
    <Box
      ref={scrollerRef}
      role="list"
      aria-busy={loading}
      aria-label={ariaLabel}
      onScroll={onScroll}
      sx={() => ({
        height,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: isHorizontal ? 'hidden' : undefined,
        // keep the styled thin scrollbar for vertical lists.
        ...(isHorizontal
          ? {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }
          : {}),
      })}
    >
      <Virtuoso
        style={{ height: '100%', width: '100%' }}
        horizontalDirection={isHorizontal}
        totalCount={items.length}
        endReached={() => {
          if (!loading && hasMore) {
            loadMore();
          }
        }}
        overscan={500}
        itemContent={(index) => renderItem(items[index], index)}
        rangeChanged={onRangeChanged}
        // Always register the same Scroller override so the `components` prop shape
        // stays stable across orientation changes (react-virtuoso caches component
        // references internally; toggling a key's presence between renders can leave
        // a stale/undefined reference, especially right after a fresh mount).
        // scroll-snap-type only affects the x axis, so it's a no-op in vertical mode.
        components={{
          Scroller: SnapScroller,
          ...(footer ? { Footer: () => <>{footer}</> } : {}),
        }}
      />
    </Box>
  );
};

export default InfiniteScroller;
